import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

import * as UI from "../../../../../modules/ui-kit";
import EntityCreateForm from "../../../components/EntityCreateForm";
import UpsertParameter from "../ParameterDetail/UpsertParameter.graphql";
import ListParameters from "../ParameterInventory/ListParameters.graphql";

const DEFAULT_VALUES = {
    about: [],
    dataType: "",
    deprecated: false,
    hidden: false,
    name: [],
    note: "",
    parameterType: "",
    unit: "",
};

function toDefinition(values) {
    const definition: Record<string, unknown> = {
        deprecated: values.deprecated,
        hidden: values.hidden,
        name: values.name,
    };
    if (values.dataType) definition.dataType = values.dataType;
    if (values.parameterType) definition.parameterType = values.parameterType;
    if (values.unit) {
        definition.unit = values.unit;
        definition.units = [values.unit];
    }
    if (values.about.length) {
        definition.about = values.about;
        definition.description = values.about;
    }
    if (values.note) definition.note = values.note;
    return definition;
}

function renderFields({ saving, setValue, values }) {
    return (
        <>
            <UI.Field>
                <UI.FieldLabel>name</UI.FieldLabel>
                <UI.FieldDescription>Multi-language translations for the parameter name</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="name"
                    onChange={ (val) => setValue("name", val) }
                    value={ values.name }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>dataType</UI.FieldLabel>
                <UI.FieldDescription>Parameter primitive data type (text, number, boolean)</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("dataType", val) }
                    placeholder="e.g. number / text / boolean"
                    value={ values.dataType }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>parameterType</UI.FieldLabel>
                <UI.FieldDescription>Domain parameter type classification</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("parameterType", val) }
                    placeholder="e.g. Length / Angle / Text"
                    value={ values.parameterType }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>units</UI.FieldLabel>
                <UI.FieldDescription>Measurement unit symbol (e.g. mm, kg, m²)</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("unit", val) }
                    placeholder="e.g. mm"
                    value={ values.unit }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>deprecated</UI.FieldLabel>
                <UI.FieldDescription>Toggle whether this parameter is deprecated</UI.FieldDescription>
                <UI.SegmentedControl
                    disabled={ saving }
                    onChange={ (val) => setValue("deprecated", val) }
                    options={ [
                        { label: "Deprecated", value: true },
                        { label: "Not deprecated", value: false },
                    ] }
                    value={ values.deprecated }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>hidden</UI.FieldLabel>
                <UI.FieldDescription>Toggle whether this parameter is hidden</UI.FieldDescription>
                <UI.SegmentedControl
                    disabled={ saving }
                    onChange={ (val) => setValue("hidden", val) }
                    options={ [
                        { label: "Hidden", value: true },
                        { label: "Visible", value: false },
                    ] }
                    value={ values.hidden }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>about</UI.FieldLabel>
                <UI.FieldDescription>Multi-language description explaining this parameter</UI.FieldDescription>
                <UI.TranslatedInput
                    disabled={ saving }
                    label="about"
                    onChange={ (val) => setValue("about", val) }
                    value={ values.about }
                />
            </UI.Field>

            <UI.Field>
                <UI.FieldLabel>note</UI.FieldLabel>
                <UI.FieldDescription>Internal notes or documentation</UI.FieldDescription>
                <UI.TextInput
                    disabled={ saving }
                    onChange={ (val) => setValue("note", val) }
                    placeholder="Internal note..."
                    value={ values.note }
                />
            </UI.Field>
        </>
    );
}

export default function ParameterNew() {
    const [upsertParameter] = useMutation(
        gql(UpsertParameter),
        {
            awaitRefetchQueries: true,
            // Document, not the query name: this screen replaces the list rather
            // than sitting beside it, so a name had no active query to match.
            refetchQueries: [
                { query: gql(ListParameters) },
            ],
        },
    );

    async function onCreate(definition) {
        const result = await upsertParameter({ variables: { definition } });
        const payload = result.data?.upsertParameter;
        return {
            errors: payload?.errors,
            redirectTo: payload?.parameter?.id && `../${ payload.parameter.id }`,
        };
    }

    return (
        <div className="section-card">
            <EntityCreateForm
                createLabel="Create Parameter"
                defaultValues={ DEFAULT_VALUES }
                jsonDescription="Raw parameter JSON definition (name, dataType, parameterType, units, deprecated, hidden, about, note)"
                onCreate={ onCreate }
                renderFields={ renderFields }
                title="New Parameter"
                toDefinition={ toDefinition }
            />
        </div>
    );
}
